// Core packages
import { ElementRef, OnDestroy, OnInit, QueryList } from "@angular/core";
import { FormControl } from "@angular/forms";
// Third party packages
import { MatDialog, MatDialogRef } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { finalize, take } from "rxjs/operators";
// Custom packages
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
/**
 * Script start
 */
/** Max length accepted by the back-end for an intervention name. */
export var INTERVENTION_NAME_MAX_LENGTH = 200;
/** Above this amount of rows the local search field is rendered. */
var INTERVENTION_SEARCH_THRESHOLD = 10;
/** Sentinel used when the document count could not be derived. */
var DOCUMENT_COUNT_UNKNOWN = -1;
/** Root i18n key of every label used by this dialog. */
var I18N_ROOT = "properties.documentsPage.interventionArchiving.";
/**
 * Manages the archive-wide list of "interventi di archiviazione".
 *
 * VISIBILITY: this dialog - and the entry point that opens it - must be
 * exposed only when the current role type is "admin" or "operator". Every
 * operation answers 403 for any other role, so showing the UI would be a dead
 * end. The check lives in the caller (see
 * PropertyDocumentsComponent.onManageInterventionArchiving), which already has
 * the role type from the user context; the dialog stays agnostic so that the
 * authorization rule has a single home.
 */
var InterventionArchivingDialogComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function InterventionArchivingDialogComponent(dialogRef, data, documentArchivesService, translateService, toastrService, confirmDialog) {
        var _this = this;
        this.dialogRef = dialogRef;
        this.data = data;
        this.documentArchivesService = documentArchivesService;
        this.translateService = translateService;
        this.toastrService = toastrService;
        this.confirmDialog = confirmDialog;
        this.subscriptions = [];
        // Exposed to the template
        this.maxLength = INTERVENTION_NAME_MAX_LENGTH;
        this.searchThreshold = INTERVENTION_SEARCH_THRESHOLD;
        this.countUnknown = DOCUMENT_COUNT_UNKNOWN;
        this.documentArchiveId = "";
        this.propertyName = "";
        /** Raw names, exactly as persisted by the back-end. */
        this.interventions = [];
        this.rows = [];
        this.displayedRows = [];
        /** Local, client-side only filter. */
        this.searchTerm = "";
        /**
         * Owned copy of the document counts, keyed by intervention name.
         *
         * It must be owned and kept in sync by every operation: the map handed over
         * by the caller is a snapshot of the archive tree taken when the dialog
         * opened, so after a rename the new name would have no entry at all and the
         * row would fall back to "unknown", re-enabling a deletion the back-end
         * refuses. Null prototype because the keys are free text typed by the users.
         */
        this.documentCounts = Object.create(null);
        // BEGIN - Create intervention form
        this.createControl = new FormControl("");
        this.createServerError = "";
        this.creating = false;
        // END - Create intervention form
        /**
         * Row with an open inline action. Only one row at a time can be in
         * `editing` or `confirmingDelete`, so a single reference is enough.
         */
        this.activeRow = null;
        /** "editing" | "confirmingDelete" | null */
        this.activeKind = null;
        this.draft = "";
        this.draftError = "";
        /** Row currently performing an API call (rename or delete). */
        this.savingRow = null;
        /**
         * Blocking error on the archive itself (403, or 404 on create/delete):
         * retrying makes no sense, so every action gets disabled.
         */
        this.contextError = "";
        /** True as soon as an operation succeeded: the caller must refresh. */
        this.changed = false;
        /**
         * Validator of the create field
         *
         * @since 1.0.0
         */
        this.nameValidator = function (control) {
            var normalized = _this.normalizeName(typeof control.value === "string" ? control.value : "");
            if (!normalized) {
                return { interventionEmpty: true };
            }
            if (normalized.length > INTERVENTION_NAME_MAX_LENGTH) {
                return { interventionTooLong: true };
            }
            if (_this.isDuplicate(normalized, null)) {
                return { interventionDuplicate: true };
            }
            return null;
        };
        // Every closing path goes through attemptClose(), so that a pending
        // rename draft can be confirmed before the dialog disappears.
        dialogRef.disableClose = true;
    }
    /**
     * Init component
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.ngOnInit = function () {
        var _this = this;
        if (this.data) {
            this.documentArchiveId = this.data.documentArchiveId
                ? this.data.documentArchiveId
                : "";
            this.propertyName = this.data.propertyName ? this.data.propertyName : "";
            // Copy the caller's snapshot before building the rows: from now on the
            // dialog owns it and never mutates the object it received
            this.copyDocumentCounts(this.data.documentCounts);
            this.setInterventions(Array.isArray(this.data.interventions) ? this.data.interventions : []);
        }
        this.createControl.setValidators([this.nameValidator]);
        this.subscriptions.push(this.createControl.valueChanges.subscribe(function () {
            // A server-side rejection is stale as soon as the value changes
            _this.createServerError = "";
            // Material only paints mat-error on a TOUCHED control: marking it here
            // surfaces the duplicate and length errors while typing, instead of
            // waiting for the blur or for the Crea click
            _this.createControl.markAsTouched();
        }));
        // ESC must cancel the open row action first and close the dialog only
        // when every row is idle
        this.subscriptions.push(this.dialogRef.keydownEvents().subscribe(function (event) {
            if (event.key !== "Escape" && event.keyCode !== 27) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            _this.onEscape();
        }));
        this.subscriptions.push(this.dialogRef.backdropClick().subscribe(function () { return _this.attemptClose(); }));
    };
    /**
     * Handle component destroy
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.ngOnDestroy = function () {
        // Unsubscribe from all subscriptions
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "createValue", {
        /**
         * Current raw value of the create field
         *
         * @since 1.0.0
         */
        get: function () {
            return typeof this.createControl.value === "string"
                ? this.createControl.value
                : "";
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "createNormalizedValue", {
        /**
         * Normalized value of the create field: this is exactly what gets sent
         *
         * @since 1.0.0
         */
        get: function () {
            return this.normalizeName(this.createValue);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "draftNormalized", {
        /**
         * Normalized value of the inline rename field: this is exactly what gets
         * sent as `to`
         *
         * @since 1.0.0
         */
        get: function () {
            return this.normalizeName(this.draft);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "actionsDisabled", {
        /**
         * True when no operation can be started (blocking context error or a
         * running API call)
         *
         * @since 1.0.0
         */
        get: function () {
            return !!this.contextError || this.savingRow !== null || this.creating;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "renameDisabled", {
        /**
         * True when the inline "Salva" button must be disabled
         *
         * @since 1.0.0
         */
        get: function () {
            if (!this.activeRow || this.savingRow !== null) {
                return true;
            }
            var to = this.draftNormalized;
            return !to || to === this.activeRow.raw;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InterventionArchivingDialogComponent.prototype, "renameImpact", {
        /**
         * Number of documents the pending rename is going to update, or
         * DOCUMENT_COUNT_UNKNOWN
         *
         * @since 1.0.0
         */
        get: function () {
            return this.activeRow ? this.activeRow.count : DOCUMENT_COUNT_UNKNOWN;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Handle the create field input
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.onCreateSubmit = function () {
        var _this = this;
        if (this.contextError || this.creating) {
            return;
        }
        this.createControl.markAsTouched();
        this.createControl.updateValueAndValidity();
        if (this.createControl.invalid) {
            return;
        }
        var name = this.createNormalizedValue;
        this.creating = true;
        this.createServerError = "";
        this.subscriptions.push(this.documentArchivesService
            .createIntervention(this.documentArchiveId, name)
            .pipe(finalize(function () { return (_this.creating = false); }))
            .subscribe(function (res) {
            if (!res || !res.status) {
                _this.createServerError = _this.translateService.instant(I18N_ROOT + "errors.unexpected");
                return;
            }
            // Always trust the persisted (trimmed) name over the sent one
            var persisted = res.data && typeof res.data.name === "string"
                ? res.data.name
                : name;
            _this.addLocal(persisted);
            _this.changed = true;
            _this.createControl.setValue("");
            _this.createControl.markAsUntouched();
            _this.toastrService.success(_this.resolveMessage(res, I18N_ROOT + "success.created"));
        }, function (err) { return _this.handleOperationError(err, "create", null); }));
    };
    /**
     * Apply the local search filter
     *
     * @since 1.0.0
     *
     * @param term New search term
     */
    InterventionArchivingDialogComponent.prototype.onSearchChange = function (term) {
        this.searchTerm = term;
        this.applyFilter();
    };
    /**
     * Start the inline rename of the given row
     *
     * @since 1.0.0
     *
     * @param row Row to rename
     */
    InterventionArchivingDialogComponent.prototype.onStartRename = function (row) {
        var _this = this;
        if (this.actionsDisabled) {
            return;
        }
        if (!this.closeActiveAction(function () { return _this.onStartRename(row); })) {
            return;
        }
        row.error = "";
        this.activeRow = row;
        this.activeKind = "editing";
        // Start from the RAW value: trimming it here would silently rewrite
        // historical names that keep trailing spaces
        this.draft = row.raw;
        this.draftError = "";
        this.applyFilter();
        this.focusRenameInput();
    };
    /**
     * Handle the inline rename field input
     *
     * @since 1.0.0
     *
     * @param value New draft value
     */
    InterventionArchivingDialogComponent.prototype.onDraftChange = function (value) {
        this.draft = typeof value === "string" ? value : "";
        this.draftError = "";
    };
    /**
     * Save the inline rename
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.onSaveRename = function () {
        var _this = this;
        if (this.contextError ||
            this.savingRow ||
            !this.activeRow ||
            this.activeKind !== "editing") {
            return;
        }
        var row = this.activeRow;
        // "from" raw and untouched, "to" normalized: see
        // DocumentArchivesService.renameIntervention
        var from = row.raw;
        var to = this.draftNormalized;
        if (!to) {
            this.draftError = this.translateService.instant(I18N_ROOT + "errors.emptyName");
            return;
        }
        if (to.length > INTERVENTION_NAME_MAX_LENGTH) {
            this.draftError = this.translateService.instant(I18N_ROOT + "errors.tooLong", { max: INTERVENTION_NAME_MAX_LENGTH });
            return;
        }
        if (to === from) {
            this.draftError = this.translateService.instant(I18N_ROOT + "errors.sameName");
            return;
        }
        if (this.isDuplicate(to, row)) {
            this.draftError = this.translateService.instant(I18N_ROOT + "errors.duplicate");
            return;
        }
        this.draftError = "";
        this.savingRow = row;
        this.subscriptions.push(this.documentArchivesService
            .renameIntervention(this.documentArchiveId, from, to)
            .pipe(finalize(function () { return (_this.savingRow = null); }))
            .subscribe(function (res) {
            if (!res || !res.status) {
                row.error = _this.translateService.instant(I18N_ROOT + "errors.unexpected");
                return;
            }
            var docsUpdated = res.data && typeof res.data.docsUpdated === "number"
                ? res.data.docsUpdated
                : DOCUMENT_COUNT_UNKNOWN;
            // Read before the rows get rebuilt: it tells whether the user was
            // expecting the cascade to touch something
            var expectedDocs = row.count;
            // Keep the count attached to the new name, otherwise the rebuilt
            // row falls back to "unknown" and re-enables the deletion
            _this.moveCount(from, to, docsUpdated);
            _this.renameLocal(from, to);
            _this.changed = true;
            _this.resetActiveAction();
            _this.restoreFocus(to);
            _this.toastrService.success(_this.buildRenameSuccessMessage(res, docsUpdated, expectedDocs));
        }, function (err) { return _this.handleOperationError(err, "rename", row); }));
    };
    /**
     * Ask for the inline deletion confirmation of the given row
     *
     * @since 1.0.0
     *
     * @param row Row to delete
     */
    InterventionArchivingDialogComponent.prototype.onStartDelete = function (row) {
        var _this = this;
        if (this.actionsDisabled || !this.canDelete(row)) {
            return;
        }
        if (!this.closeActiveAction(function () { return _this.onStartDelete(row); })) {
            return;
        }
        row.error = "";
        this.activeRow = row;
        this.activeKind = "confirmingDelete";
        this.draft = "";
        this.draftError = "";
        this.applyFilter();
    };
    /**
     * Confirm the inline deletion
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.onConfirmDelete = function () {
        var _this = this;
        if (this.contextError ||
            this.savingRow ||
            !this.activeRow ||
            this.activeKind !== "confirmingDelete") {
            return;
        }
        var row = this.activeRow;
        this.savingRow = row;
        this.subscriptions.push(this.documentArchivesService
            .deleteIntervention(this.documentArchiveId, row.raw)
            .pipe(finalize(function () { return (_this.savingRow = null); }))
            .subscribe(function (res) {
            if (!res || !res.status) {
                row.error = _this.translateService.instant(I18N_ROOT + "errors.unexpected");
                return;
            }
            _this.forgetCount(row.raw);
            _this.removeLocal(row.raw);
            _this.changed = true;
            _this.resetActiveAction();
            _this.toastrService.success(_this.resolveMessage(res, I18N_ROOT + "success.deleted"));
        }, function (err) { return _this.handleOperationError(err, "delete", row); }));
    };
    /**
     * Cancel the open inline action, asking for confirmation when the rename
     * draft holds unsaved changes
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.onCancelRowAction = function () {
        var _this = this;
        if (this.savingRow) {
            return;
        }
        var previousRaw = this.activeRow ? this.activeRow.raw : null;
        if (this.hasPendingDraft()) {
            this.confirmDiscardDraft(function () {
                _this.resetActiveAction();
                _this.restoreFocus(previousRaw);
            });
            return;
        }
        this.resetActiveAction();
        this.restoreFocus(previousRaw);
    };
    /**
     * Handle the ESC key: it cancels the open row action first and closes the
     * dialog only when every row is idle
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.onEscape = function () {
        if (this.savingRow || this.creating) {
            return;
        }
        if (this.activeRow) {
            this.onCancelRowAction();
            return;
        }
        this.attemptClose();
    };
    /**
     * Close the dialog, asking for confirmation when the rename draft holds
     * unsaved changes. The caller receives `true` when the archive changed and
     * must be re-fetched.
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.attemptClose = function () {
        var _this = this;
        if (this.savingRow || this.creating) {
            return;
        }
        if (this.hasPendingDraft()) {
            this.confirmDiscardDraft(function () { return _this.dialogRef.close(_this.changed); });
            return;
        }
        this.dialogRef.close(this.changed);
    };
    /**
     * True when the given row can be deleted.
     *
     * A known count greater than zero means the back-end will certainly refuse
     * the deletion. An unknown or zero count still allows the attempt, because
     * deleted documents are not visible in the archive tree (the 409 is handled).
     *
     * @since 1.0.0
     *
     * @param row Row to check
     */
    InterventionArchivingDialogComponent.prototype.canDelete = function (row) {
        if (this.contextError) {
            return false;
        }
        return row.count <= 0;
    };
    /**
     * Init/replace the managed list
     *
     * @since 1.0.0
     *
     * @param interventions Raw names coming from the back-end
     */
    InterventionArchivingDialogComponent.prototype.setInterventions = function (interventions) {
        var unique = [];
        interventions.forEach(function (item) {
            var raw = typeof item === "string" ? item : String(item);
            // Duplicated raw values would be indistinguishable for every API call
            // (they are all name-based), so a single row is kept for each
            if (unique.indexOf(raw) === -1) {
                unique.push(raw);
            }
        });
        this.interventions = unique;
        this.buildRows();
    };
    /**
     * Rebuild the rows out of the raw list, sorted alphabetically with a stable
     * fallback on the back-end order
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.buildRows = function () {
        var _this = this;
        var decorated = this.interventions.map(function (raw, index) {
            return { raw: raw, index: index };
        });
        // Array.prototype.sort is not guaranteed to be stable on ES5 targets:
        // the original index keeps ties in the back-end order
        decorated.sort(function (a, b) {
            var compared = a.raw.trim().localeCompare(b.raw.trim());
            return compared !== 0 ? compared : a.index - b.index;
        });
        var placeholder = this.translateService.instant(I18N_ROOT + "unnamed");
        this.rows = decorated.map(function (item) {
            var trimmed = item.raw.trim();
            return {
                raw: item.raw,
                display: trimmed ? trimmed : placeholder,
                blank: !trimmed,
                count: _this.resolveCount(item.raw),
                error: "",
            };
        });
        this.applyFilter();
    };
    /**
     * Recompute the displayed rows out of the local search term. The row with
     * an open action is always kept visible.
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.applyFilter = function () {
        var _this = this;
        var term = this.searchTerm ? this.searchTerm.trim().toLowerCase() : "";
        if (!term) {
            this.displayedRows = this.rows.slice();
            return;
        }
        this.displayedRows = this.rows.filter(function (row) {
            return (row === _this.activeRow || row.display.toLowerCase().indexOf(term) > -1);
        });
    };
    /**
     * Take an owned copy of the counts snapshot handed over by the caller
     *
     * @since 1.0.0
     *
     * @param source Map of counts keyed by intervention name
     */
    InterventionArchivingDialogComponent.prototype.copyDocumentCounts = function (source) {
        var _this = this;
        this.documentCounts = Object.create(null);
        if (!source || typeof source !== "object") {
            return;
        }
        Object.keys(source).forEach(function (key) {
            var value = source[key];
            if (typeof value === "number" && value >= 0) {
                _this.documentCounts[key] = value;
            }
        });
    };
    /**
     * Resolve the document count of the given raw name
     *
     * @since 1.0.0
     *
     * @param raw Raw intervention name
     */
    InterventionArchivingDialogComponent.prototype.resolveCount = function (raw) {
        // The tree groups documents by the value stored on the document, which
        // may differ from the archive entry by surrounding spaces only
        var candidates = [raw, raw.trim()];
        for (var i = 0; i < candidates.length; i++) {
            if (Object.prototype.hasOwnProperty.call(this.documentCounts, candidates[i])) {
                var value = this.documentCounts[candidates[i]];
                if (typeof value === "number" && value >= 0) {
                    return value;
                }
            }
        }
        return DOCUMENT_COUNT_UNKNOWN;
    };
    /**
     * Move the document count from the old name to the new one after a rename.
     *
     * `docsUpdated` is authoritative when the back-end returns it: it counts
     * every document the cascade touched, deleted ones included, while the
     * tree-derived count is only a lower bound. When it is missing, the previous
     * count is carried over so that a populated intervention keeps its deletion
     * blocked instead of silently falling back to "unknown".
     *
     * Only the exact `from` key is dropped: a sibling row whose trimmed value
     * happens to match the same tree key must keep its own count.
     *
     * @since 1.0.0
     *
     * @param from Previous raw name
     * @param to New name
     * @param docsUpdated Documents updated by the cascade, or
     *                    DOCUMENT_COUNT_UNKNOWN
     */
    InterventionArchivingDialogComponent.prototype.moveCount = function (from, to, docsUpdated) {
        var previous = this.resolveCount(from);
        var next = docsUpdated !== DOCUMENT_COUNT_UNKNOWN ? docsUpdated : previous;
        if (Object.prototype.hasOwnProperty.call(this.documentCounts, from)) {
            delete this.documentCounts[from];
        }
        if (next !== DOCUMENT_COUNT_UNKNOWN) {
            this.documentCounts[to] = next;
        }
    };
    /**
     * Drop the document count of a name that no longer exists
     *
     * @since 1.0.0
     *
     * @param name Raw name to forget
     */
    InterventionArchivingDialogComponent.prototype.forgetCount = function (name) {
        if (Object.prototype.hasOwnProperty.call(this.documentCounts, name)) {
            delete this.documentCounts[name];
        }
    };
    /**
     * Canonical form of a name typed by the user:
     * - every run of whitespace collapses into a single space and the edges are
     *   trimmed, so "primo     nome" becomes "primo nome";
     * - the result is uppercased, so "prova" is stored as "PROVA".
     *
     * This is the ONLY transform applied to an outgoing name, and it is applied
     * to the create payload and to the rename `to` - NEVER to the rename `from`,
     * which must stay byte-identical to the value stored in the archive.
     *
     * `toUpperCase()` and not `toLocaleUpperCase()`: the result gets persisted,
     * so it must not depend on the browser locale (a Turkish locale would turn
     * "i" into "İ").
     *
     * `\s` also covers tabs and the non-breaking spaces that come with a paste
     * from Word, and the input is capped at 200 chars, so the regex cannot be
     * fed an unbounded string. Note that uppercasing can still make the value
     * longer than what was typed ("ß" becomes "SS"): that is why the length
     * check runs on the normalized value, not on the raw one.
     *
     * @since 1.0.0
     *
     * @param value Raw value typed by the user
     */
    InterventionArchivingDialogComponent.prototype.normalizeName = function (value) {
        if (typeof value !== "string") {
            return "";
        }
        return value
            .replace(/\s+/g, " ")
            .trim()
            .toUpperCase();
    };
    /**
     * Duplicate check against the managed list.
     *
     * Both sides are compared in their NORMALIZED form, which is uppercase by
     * construction: "Prova", "prova" and "prova " therefore all collide with an
     * existing "PROVA" and are refused before any request leaves the browser.
     * The back-end rejects them too, so accepting them client-side would only
     * cost a round trip and show a remote error instead of a field error.
     *
     * Normalized values are compared directly instead of lowercasing them again:
     * the uppercase/lowercase round trip is not an identity for every character
     * ("ß" uppercases to "SS").
     *
     * @since 1.0.0
     *
     * @param candidate Normalized candidate name
     * @param exclude Row to skip (the one being renamed)
     */
    InterventionArchivingDialogComponent.prototype.isDuplicate = function (candidate, exclude) {
        var _this = this;
        return this.rows.some(function (row) {
            if (exclude && row === exclude) {
                return false;
            }
            return _this.normalizeName(row.raw) === candidate;
        });
    };
    /**
     * True when the open rename holds unsaved changes
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.hasPendingDraft = function () {
        return (this.activeKind === "editing" &&
            this.activeRow !== null &&
            this.draft !== this.activeRow.raw);
    };
    /**
     * Close the open row action before opening another one.
     *
     * Returns true when the caller can proceed synchronously, false when a
     * discard confirmation has been opened (`onDone` runs once confirmed).
     *
     * @since 1.0.0
     *
     * @param onDone Callback to run after a confirmed discard
     */
    InterventionArchivingDialogComponent.prototype.closeActiveAction = function (onDone) {
        var _this = this;
        if (!this.activeRow) {
            return true;
        }
        if (!this.hasPendingDraft()) {
            this.resetActiveAction();
            return true;
        }
        this.confirmDiscardDraft(function () {
            _this.resetActiveAction();
            onDone();
        });
        return false;
    };
    /**
     * Ask the user whether the pending rename draft can be discarded
     *
     * @since 1.0.0
     *
     * @param onConfirm Callback to run when the user confirms
     */
    InterventionArchivingDialogComponent.prototype.confirmDiscardDraft = function (onConfirm) {
        var dialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant(I18N_ROOT + "discard.title"),
                message: this.translateService.instant(I18N_ROOT + "discard.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        this.subscriptions.push(dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (res) {
                onConfirm();
            }
        }));
    };
    /**
     * Reset the open row action
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.resetActiveAction = function () {
        this.activeRow = null;
        this.activeKind = null;
        this.draft = "";
        this.draftError = "";
        this.applyFilter();
    };
    /**
     * Add the given name to the local list
     *
     * @since 1.0.0
     *
     * @param name Persisted name
     */
    InterventionArchivingDialogComponent.prototype.addLocal = function (name) {
        if (this.interventions.indexOf(name) === -1) {
            this.interventions.push(name);
        }
        this.buildRows();
    };
    /**
     * Replace `from` with `to` in the local list
     *
     * @since 1.0.0
     *
     * @param from Previous raw name
     * @param to New name
     */
    InterventionArchivingDialogComponent.prototype.renameLocal = function (from, to) {
        var index = this.interventions.indexOf(from);
        if (index > -1) {
            this.interventions[index] = to;
        }
        else if (this.interventions.indexOf(to) === -1) {
            this.interventions.push(to);
        }
        this.buildRows();
    };
    /**
     * Remove the given name from the local list
     *
     * @since 1.0.0
     *
     * @param name Raw name to remove
     */
    InterventionArchivingDialogComponent.prototype.removeLocal = function (name) {
        var index = this.interventions.indexOf(name);
        if (index > -1) {
            this.interventions.splice(index, 1);
        }
        this.buildRows();
    };
    /**
     * Re-fetch the archive to realign the local list after a concurrent change.
     *
     * `preserveRaw`/`preserveMessage` re-attach an inline error to the reloaded
     * row, so that the reason of the failure is not lost by the refresh.
     *
     * @since 1.0.0
     *
     * @param [preserveRaw] Raw name whose inline error must survive the reload
     * @param [preserveMessage] Inline error message to re-attach
     */
    InterventionArchivingDialogComponent.prototype.reloadInterventions = function (preserveRaw, preserveMessage) {
        var _this = this;
        // The archive changed under us: the caller must re-fetch it as well
        this.changed = true;
        if (!this.documentArchiveId) {
            return;
        }
        // The caller's reader when it handed one over (the 3.0.0 dossier),
        // otherwise the legacy archive, as always
        var source = this.data && this.data.reloadNames
            ? this.data.reloadNames()
            : this.documentArchivesService.get(this.documentArchiveId);
        this.subscriptions.push(source.subscribe(function (res) {
            if (!res ||
                !res.status ||
                !res.data ||
                !Array.isArray(res.data.nameInterventionArchiving)) {
                return;
            }
            _this.resetActiveAction();
            _this.setInterventions(res.data.nameInterventionArchiving);
            _this.createControl.updateValueAndValidity();
            if (typeof preserveRaw === "string") {
                var target = _this.rows.filter(function (row) { return row.raw === preserveRaw; })[0];
                if (target) {
                    target.error = preserveMessage;
                }
            }
        }, function () {
            // Either reader already notifies the user (@see InterventionArchivingDialogData.reloadNames)
        }));
    };
    /**
     * Map an API failure to a user-facing message and place it where it belongs.
     *
     * The back-end `message` is deliberately ignored: it is already translated
     * server-side following the SERVER configuration, not the request language.
     * Branching happens on status code + operation only.
     *
     * @since 1.0.0
     *
     * @param err Error response
     * @param operation "create" | "rename" | "delete"
     * @param row Row the operation belongs to (null on create)
     */
    InterventionArchivingDialogComponent.prototype.handleOperationError = function (err, operation, row) {
        var status = err && typeof err.status === "number" ? err.status : 0;
        // A 403 means either that the requested role is not admin/operator, or
        // that the archive does not exist. Neither is retriable from here: since
        // the entry point is already gated on the role, in practice it shows up
        // only when the archive is gone or the active role changed underneath.
        if (status === 403) {
            this.contextError = this.translateService.instant(I18N_ROOT + "errors.forbidden");
            this.resetActiveAction();
            return;
        }
        // On create and delete a 404 means the archive (or the property) does not
        // exist: nothing can be retried. On rename it is ambiguous, because it
        // also covers a "from" that no longer exists, so there the list is just
        // reloaded instead of locking the whole dialog.
        if (status === 404 && operation !== "rename") {
            this.contextError = this.translateService.instant(I18N_ROOT + "errors.archiveNotFound");
            this.resetActiveAction();
            return;
        }
        var message = "";
        var reload = false;
        if (status === 404) {
            message = this.translateService.instant(I18N_ROOT + "errors.renameSourceNotFound");
            reload = true;
        }
        else if (status === 409) {
            if (operation === "delete") {
                message = this.translateService.instant(I18N_ROOT + "errors.deleteHasDocuments");
            }
            else {
                // Somebody else created that name in the meantime
                message = this.translateService.instant(I18N_ROOT + "errors.duplicateRemote");
                reload = true;
            }
        }
        else if (status === 400) {
            message = this.translateService.instant(I18N_ROOT +
                (this.hasFieldErrors(err) ? "errors.invalidName" : "errors.rejectedName"));
        }
        else {
            message = this.translateService.instant(I18N_ROOT + "errors.unexpected");
        }
        if (operation === "create") {
            this.createServerError = message;
            if (reload) {
                this.reloadInterventions();
            }
            return;
        }
        if (row) {
            row.error = message;
        }
        if (reload) {
            this.reloadInterventions(row ? row.raw : undefined, message);
        }
    };
    /**
     * True when the error payload carries field validation errors
     *
     * @since 1.0.0
     *
     * @param err Error response
     */
    InterventionArchivingDialogComponent.prototype.hasFieldErrors = function (err) {
        return !!(err &&
            err.error &&
            err.error.errors &&
            typeof err.error.errors === "object");
    };
    /**
     * Build the rename success message, exposing how many documents the cascade
     * actually updated
     *
     * @since 1.0.0
     *
     * @param res API response
     * @param docsUpdated Amount of updated documents, or DOCUMENT_COUNT_UNKNOWN
     * @param expectedDocs Amount of documents the row was showing before the
     *                     rename, or DOCUMENT_COUNT_UNKNOWN
     */
    InterventionArchivingDialogComponent.prototype.buildRenameSuccessMessage = function (res, docsUpdated, expectedDocs) {
        if (docsUpdated === DOCUMENT_COUNT_UNKNOWN) {
            return this.resolveMessage(res, I18N_ROOT + "success.renamed");
        }
        if (docsUpdated === 0) {
            // A zero cascade is only worth reporting when the row WAS showing
            // documents: it means the name stored on them is not byte-identical to
            // the one in the archive list, so the rename did not reach them.
            //
            // An unknown count must NOT raise the warning: the count is derived from
            // the archive tree, which only holds groups with at least one document,
            // so every empty intervention (a freshly created one, typically) is
            // unknown and renaming it legitimately updates nothing.
            if (expectedDocs > 0) {
                return this.translateService.instant(I18N_ROOT + "success.renamedNoDocs");
            }
            return this.resolveMessage(res, I18N_ROOT + "success.renamed");
        }
        return this.translateService.instant(I18N_ROOT + "success.renamedWithDocs", {
            count: docsUpdated,
        });
    };
    /**
     * Use the back-end message when available, fall back on a local key
     *
     * @since 1.0.0
     *
     * @param res API response
     * @param fallbackKey i18n key used when the response carries no message
     */
    InterventionArchivingDialogComponent.prototype.resolveMessage = function (res, fallbackKey) {
        if (res && typeof res.message === "string" && res.message) {
            return res.message;
        }
        return this.translateService.instant(fallbackKey);
    };
    /**
     * Move the focus into the inline rename field
     *
     * @since 1.0.0
     */
    InterventionArchivingDialogComponent.prototype.focusRenameInput = function () {
        var _this = this;
        setTimeout(function () {
            if (_this.renameInputs && _this.renameInputs.first) {
                _this.renameInputs.first.nativeElement.focus();
            }
        }, 0);
    };
    /**
     * Give the focus back to the rename button of the given row, once every row
     * is rendered in its idle state again
     *
     * @since 1.0.0
     *
     * @param raw Raw name of the row that owned the closed action
     */
    InterventionArchivingDialogComponent.prototype.restoreFocus = function (raw) {
        var _this = this;
        if (typeof raw !== "string") {
            return;
        }
        setTimeout(function () {
            var index = _this.displayedRows
                .map(function (row) { return row.raw; })
                .indexOf(raw);
            var triggers = _this.renameTriggers ? _this.renameTriggers.toArray() : [];
            if (index > -1 && triggers[index]) {
                triggers[index].nativeElement.focus();
            }
        }, 0);
    };
    return InterventionArchivingDialogComponent;
}());
export { InterventionArchivingDialogComponent };
